import React, { useContext } from 'react';
import { SnackbarProvider, useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

const MessageContext = React.createContext(null);

const CloseButton = ({ onClick }) => (
  <IconButton size="small" aria-label="close" color="inherit" onClick={onClick}>
    <CloseIcon fontSize="small" />
  </IconButton>
);

const useStyles = makeStyles(() => ({
  variantSuccess: { cursor: 'pointer' },
  // Error message may be quite big and multi-line, make sure we preserve the whitespace
  variantError: { cursor: 'pointer', whiteSpace: 'pre-line' },
  // Warning message may be quite big and multi-line, make sure we preserve the whitespace
  variantWarning: { cursor: 'pointer', whiteSpace: 'pre-line' }
}));

const useIconStyles = makeStyles(() => ({
  root: { width: 20, height: 20, marginRight: 5 }
}));

const MessageComponent = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const callSnackbar = (message, variant, persist = false, center = false) => {
    return enqueueSnackbar(message, {
      persist,
      variant,
      anchorOrigin: { horizontal: center ? 'center' : 'right', vertical: 'top' },
      action: key => <CloseButton onClick={() => closeSnackbar(key)} />,
      'data-test': `snackbar-${variant}`
    });
  };

  const successMessage = message => callSnackbar(message, 'success');
  const errorMessage = message => callSnackbar(message, 'error', true);
  const infoMessage = message => callSnackbar(message, 'info');
  const warningMessage = (message, persist, center) => callSnackbar(message, 'warning', persist, center);

  return (
    <MessageContext.Provider
      value={{
        successMessage,
        errorMessage,
        infoMessage,
        warningMessage,
        closeSnackbar
      }}
    >
      {children}
    </MessageContext.Provider>
  );
};

const MessageWrapper = ({ children }) => {
  const classes = useStyles();
  const iconClasses = useIconStyles();
  return (
    <SnackbarProvider
      iconVariant={{
        success: <CheckCircleOutlineOutlinedIcon classes={iconClasses} />,
        error: <ErrorOutlineOutlinedIcon classes={iconClasses} />,
        info: <InfoOutlinedIcon classes={iconClasses} />,
        warning: <ReportProblemOutlinedIcon classes={iconClasses} />
      }}
      classes={classes}
      maxSnack={10}
      autoHideDuration={3000}
      disableWindowBlurListener
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <MessageComponent>{children}</MessageComponent>
    </SnackbarProvider>
  );
};

export default MessageWrapper;

export const useMessage = () => {
  return useContext(MessageContext);
};

MessageComponent.propTypes = {
  children: PropTypes.object
};

MessageWrapper.propTypes = {
  children: PropTypes.object
};
